import React, { createContext, useReducer } from 'react';
import globalReducer from '../reducers/globalReducer';
import types from '../types';
import utils from '../utils';

const {
  SET_GLOBAL_ERROR,
  SET_ERROR,
  SET_LOADING,
  SET_DONE,
  SET_ADMIN,
  SET_AUTH_ERROR,
  SET_FEEDBACK_SUCCESS,
} = types;

const { setCookie, getCookie, deleteCookie } = utils;

const initialState = {
  admin: {},
  error: false,
  globalError: false,
  loading: false,
  initLoading: true,
  done: false,
  authError: false,
  feedbackSuccess: false,
};

export const GlobalContext = createContext(initialState);

export const GlobalProvider = ({ children }) => {
  const [state, dispatch] = useReducer(globalReducer, initialState);
  const uri = 'https://liontaxi-api.emmalion.com/api';

  // actions
  const loginAdmin = async (username, password) => {
    toFalse(SET_DONE);
    try {
      setLoading(true);
      const res = await fetch(`${uri}/admins/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ username: username.toLowerCase(), password }),
      });

      const jsonRes = await res.json();
      if (res.ok) {
        setCookie('access_token', jsonRes.token);
        setDone('/admin/dashboard');
      } else {
        setError(jsonRes.message);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setGlobalError('Failed to connect with our servers');
    }
  };

  const registerAdmin = async (username, password, token) => {
    toFalse(SET_DONE);
    try {
      setLoading(true);
      const res = await fetch(`${uri}/admins/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          username: username.toLowerCase(),
          password,
          token,
        }),
      });

      const jsonRes = await res.json();
      if (res.ok) {
        setCookie('access_token', jsonRes.token);
        setDone('/admin/dashboard');
      } else {
        if (jsonRes.statusCode === 401) setGlobalError(jsonRes.message);
        else setError(jsonRes.message);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setGlobalError('Failed to connect with our servers');
    }
  };

  const getAdmin = async () => {
    toFalse(SET_AUTH_ERROR);
    toFalse(SET_GLOBAL_ERROR);
    const access_token = getCookie('_access_token_');
    if (!access_token) {
      setGlobalError('Your session has expired');
      return logAdminOut();
    }
    try {
      setLoading(true);
      const res = await fetch(`${uri}/admin`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          access_token,
        },
      });

      const jsonRes = await res.json();
      if (res.ok) {
        setDispatch(SET_ADMIN, jsonRes);
      } else {
        if (jsonRes.statusCode === 401) {
          logAdminOut();
          setGlobalError('Your session has expired');
        } else setGlobalError(jsonRes.message);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setGlobalError('Failed to connect with our servers');
    }
  };

  const addFeedback = async (name, email, feedback) => {
    setLoading(true);
    toFalse(SET_FEEDBACK_SUCCESS);
    try {
      const res = await fetch(`${uri}/feedbacks/new`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ name, email, feedback }),
      });

      const jsonRes = await res.json();
      if (res.ok) {
        setDispatch(SET_FEEDBACK_SUCCESS, true);
      } else {
        setGlobalError(jsonRes.message);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setGlobalError('Connection error');
    }
  };

  // constants and utils
  const setDispatch = (type, payload) => {
    dispatch({
      type,
      payload,
    });
  };

  const setDone = (nextRoute) => {
    setDispatch(SET_DONE, nextRoute);
  };

  const logAdminOut = () => {
    deleteCookie('access_token');
    setDispatch(SET_AUTH_ERROR, true);
  };

  // set any desired state to false via its type
  const toFalse = (type) => {
    setDispatch(type, false);
  };

  const setError = (error) => {
    setDispatch(SET_ERROR, error);
  };

  const setGlobalError = (error) => {
    setDispatch(SET_GLOBAL_ERROR, error);
  };

  const setLoading = (bool) => {
    setDispatch(SET_LOADING, bool);
  };

  return (
    <GlobalContext.Provider
      value={{
        admin: state.admin,
        globalError: state.globalError,
        error: state.error,
        loading: state.loading,
        initLoading: state.initLoading,
        done: state.done,
        authError: state.authError,
        feedbackSuccess: state.feedbackSuccess,
        loginAdmin,
        registerAdmin,
        setError,
        setGlobalError,
        getAdmin,
        addFeedback,
        toFalse,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};
